import React from "react";

import { Box, Typography } from "@material-ui/core";
import clsx from "clsx";
import copy from "copy-to-clipboard";

import { MopButton } from "client/components";
import { useLocalStorageState, useRunOnce } from "client/hooks";
import { logAmplitudeEvent } from "client/lib/amplitude";
import { CASH_SHOPIFY_LINK } from "client/pages/OnboardingStatus/utils";

import { CopyKey } from "./components/CopyKey";
import { Step } from "./components/Step";
import { useStyles } from "./styles";
import type { PlatformIntegration } from "./types";

export const CashPayIntegration: React.FC<PlatformIntegration> = ({
  handleClickVerifyGatewayEnabled,
  merchantId,
  secretKey,
}) => {
  const [integrationStage, setIntegrationStage] = useLocalStorageState(
    "cashPayIntegrationStage",
    "SignIn"
  );

  const classes = useStyles();

  useRunOnce(() =>
    logAmplitudeEvent("CASH_STATUS_PAGE", "cash_integration_card_loaded")
  );

  return (
    <Box>
      <Box ml={1}>
        <Step isActive={true} isComplete={integrationStage !== "SignIn"}>
          <Typography
            className={clsx(
              classes.header,
              integrationStage === "SignIn" && classes.activeHeader
            )}
            variant="body2"
          >
            Install Cash App Pay from the Shopify App Store
          </Typography>
          <Box className={classes.formText}>
            <ol className={classes.shopifyIntegrationInstallInstructions}>
              <li>
                Click the “Install” button (you may be prompted to login to
                Shopify first)
              </li>
              <li>On the next screen, choose “Install app”</li>
            </ol>
          </Box>
          <Box mt={1}>
            <MopButton
              className={clsx(classes.shortButton, {
                [classes.disabledButton]: integrationStage !== "SignIn",
              })}
              mopVariant={integrationStage === "SignIn" ? "primary" : "default"}
              onClick={() => {
                setIntegrationStage("CopyAndPaste");
                window.open(CASH_SHOPIFY_LINK, "_blank");
                logAmplitudeEvent(
                  "CASH_STATUS_PAGE",
                  "cash_integration_card_shopify_clicked",
                  {
                    merchantId: merchantId ?? "",
                  }
                );
              }}
            >
              Go to Shopify
            </MopButton>
          </Box>
        </Step>
        <Step
          isActive={["CopyAndPaste", "Verify", "Complete"].includes(
            integrationStage
          )}
          isComplete={["Verify", "Complete"].includes(integrationStage)}
        >
          <Typography
            className={clsx(
              classes.header,
              integrationStage === "CopyAndPaste" && classes.activeHeader
            )}
            variant="body2"
          >
            Connect to Cash App Pay
          </Typography>
          <Box className={classes.formText}>
            Continue by selecting the button “Connect to Cash App Pay”
          </Box>
        </Step>
        <Step
          isActive={["CopyAndPaste", "Verify", "Complete"].includes(
            integrationStage
          )}
          isComplete={["Verify", "Complete"].includes(integrationStage)}
        >
          <>
            <Typography
              className={clsx(
                classes.header,
                integrationStage === "CopyAndPaste" && classes.activeHeader
              )}
              variant="body2"
            >
              Link your account
            </Typography>
            <Box className={classes.formText}>
              Copy and paste your credentials (Merchant ID and Security ID),
              then click “Continue”
            </Box>
            <Box display="flex" gridGap={8} flexWrap="wrap">
              <CopyKey
                copyValue={merchantId}
                header="Merchant ID"
                isActive={integrationStage === "CopyAndPaste"}
                handleClick={() => {
                  copy(merchantId);
                  logAmplitudeEvent(
                    "CASH_STATUS_PAGE",
                    "cash_integration_card_shopify_mid_copied",
                    {
                      merchantId: merchantId ?? "",
                    }
                  );
                }}
              />
              <CopyKey
                copyValue={`•••••••••••`}
                handleClick={() => {
                  copy(secretKey);
                  setIntegrationStage("Verify");
                  logAmplitudeEvent(
                    "CASH_STATUS_PAGE",
                    "cash_integration_card_shopify_key_copied",
                    {
                      merchantId: merchantId ?? "",
                    }
                  );
                }}
                header="Secret Key"
                isActive={integrationStage === "CopyAndPaste"}
              />
            </Box>
          </>
        </Step>
        <Step
          isActive={["Verify", "Complete"].includes(integrationStage)}
          isComplete={integrationStage === "Complete"}
        >
          <Typography
            className={clsx(
              classes.header,
              integrationStage === "Verify" && classes.activeHeader
            )}
            variant="body2"
          >
            Activate and finish set-up
          </Typography>
          <Box className={classes.formText}>
            Choose “Activate Cash App Pay” located in the bottom right corner.
            You will be able to accept payments right away. You can only
            activate Cash App Pay if you have permissions to edit payment
            methods in Shopify.
          </Box>
          <Box className={classes.formText}>
            If you do not have the relevant permissions on Shopify, please
            contact the person in your organization who has access.
          </Box>
        </Step>
        <Step
          containerProps={{
            className: classes.lastStep,
          }}
          isActive={["Verify", "Complete"].includes(integrationStage)}
          isComplete={integrationStage === "Complete"}
        >
          <Typography
            className={clsx(
              classes.header,
              integrationStage === "Verify" && classes.activeHeader
            )}
            variant="body2"
          >
            Success! Confirm you have added Cash App Pay
          </Typography>
          <Box mt={1}>
            <MopButton
              className={classes.shortButton}
              mopVariant={
                ["Verify", "Complete"].includes(integrationStage)
                  ? "primary"
                  : "secondary"
              }
              onClick={() => {
                handleClickVerifyGatewayEnabled();
                setIntegrationStage("Complete");
                logAmplitudeEvent(
                  "CASH_STATUS_PAGE",
                  "cash_integration_card_complete_clicked",
                  {
                    merchantId: merchantId ?? "",
                  }
                );
              }}
              disabled={!["Verify", "Complete"].includes(integrationStage)}
            >
              Close task
            </MopButton>
          </Box>
        </Step>
      </Box>
    </Box>
  );
};
